/**
 * 🎵 MODAL DE OBRAS SEM TÍTULO - VERSÃO ATUAL
 * 
 * Exibe aviso quando o usuário tem obras que precisam de atenção:
 * - Obras sem título
 * - Obras com título genérico
 * - Obras com título auto-sugerido do arquivo
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const WorksNeedAttentionModal = ({ isOpen, onClose, worksCount }) => {
  const navigate = useNavigate();
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    if (isOpen && worksCount > 0) {
      fetchWorksNeedingAttention();
    }
  }, [isOpen, worksCount]);

  const fetchWorksNeedingAttention = async () => {
    try {
      setLoading(true);
      const response = await api.get('/works/check/needs-attention');
      setWorks(response.data.works || []);
    } catch (error) {
      console.error('Erro ao buscar obras sem título:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTitle = async (workId, newTitle) => {
    if (!newTitle || newTitle.trim().length < 2) {
      alert('O título deve ter pelo menos 2 caracteres.');
      return;
    }

    try {
      setUpdating(prev => ({ ...prev, [workId]: true }));
      await api.put(`/works/${workId}`, { title: newTitle.trim() });
      
      // Remover da lista
      setWorks(prev => prev.filter(w => w.work_id !== workId));
      
      // Se não há mais obras, fechar modal
      if (works.length <= 1) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao atualizar título:', error);
      alert('Erro ao atualizar título. Tente novamente.');
    } finally {
      setUpdating(prev => ({ ...prev, [workId]: false }));
    }
  };

  const handleGoToWork = (workId) => {
    onClose();
    navigate(`/obras/${workId}`);
  };

  const handleSkip = () => {
    // Salvar no localStorage que o usuário pulou este aviso
    localStorage.setItem('works_attention_skipped', new Date().toISOString());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={handleSkip}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-gradient-to-b from-gray-900 to-gray-950 border border-amber-500/30 shadow-2xl transition-all">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 px-6 py-4 border-b border-amber-500/20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-full">
                <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  Atenção: Obras sem Título
                </h3>
                <p className="text-sm text-amber-200/70">
                  {worksCount} obra{worksCount > 1 ? 's precisam' : ' precisa'} da sua atenção
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : works.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-green-400 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-white font-medium">Todas as obras estão com título!</p>
                <p className="text-gray-400 text-sm mt-1">Nenhuma ação necessária.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-300 text-sm mb-4">
                  Para proteger seus direitos autorais, é importante que suas obras tenham títulos claros e identificáveis.
                </p>
                
                {works.map((work) => (
                  <WorkItem 
                    key={work.work_id}
                    work={work}
                    onUpdate={handleUpdateTitle}
                    onGoToWork={handleGoToWork}
                    updating={updating[work.work_id]}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-900/50 px-6 py-4 border-t border-gray-700/50 flex justify-between gap-3">
            <button
              onClick={handleSkip}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              Fazer depois
            </button>
            <button
              onClick={() => {
                onClose();
                navigate('/obras');
              }}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors text-sm"
            >
              Ver Minhas Obras
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente para cada obra
const WorkItem = ({ work, onUpdate, onGoToWork, updating }) => {
  const [editMode, setEditMode] = useState(false);
  const [newTitle, setNewTitle] = useState(work.suggested_title || work.current_title || '');

  const handleSave = () => {
    onUpdate(work.work_id, newTitle);
    setEditMode(false);
  };

  const handleUseSuggestion = () => {
    if (work.suggested_title) {
      onUpdate(work.work_id, work.suggested_title);
    }
  };

  return (
    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {/* Título atual */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">
              {work.reason}
            </span>
          </div>
          
          {editMode ? (
            <div className="space-y-2">
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Digite o título da obra"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={updating}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium disabled:opacity-50"
                >
                  {updating ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-white font-medium">
                {work.current_title || <span className="text-gray-500 italic">Sem título</span>}
              </p>
              
              {/* Sugestão */}
              {work.suggested_title && work.suggested_title !== work.current_title && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-400">Sugestão:</span>
                  <span className="text-sm text-amber-300 font-medium">"{work.suggested_title}"</span>
                  <button
                    onClick={handleUseSuggestion}
                    disabled={updating}
                    className="px-2 py-0.5 bg-amber-600/30 hover:bg-amber-600/50 text-amber-300 rounded text-xs transition-colors disabled:opacity-50"
                  >
                    {updating ? '...' : 'Usar'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Ações */}
        {!editMode && (
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(true)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Editar título"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              onClick={() => onGoToWork(work.work_id)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Ver obra"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorksNeedAttentionModal;
